// ============================================================================
// FILE EXPLORER MIGRATION HELPERS — Phase A of file-explorer-redesign
// ----------------------------------------------------------------------------
// Pure-logic migration steps invoked from db.js upgrade callbacks. Extracted
// here so the migration body can be unit-tested with a synthetic transaction
// without spinning up a real Dexie connection (QM testing convention is
// `vi.mock('../services/db', ...)`; see batch-queue.test.js:65-117 for the
// established fake-Dexie pattern).
//
// See:
// - docs/file-explorer-redesign/PHASE-A-BINDING-SPEC.md §3.1
// - docs/file-explorer-redesign/INVESTIGATION-REPORT.md (Council seats 1-7)
// - DECISION-LOG.md DEC-FE-008
// ============================================================================

// Minimal structural shape of a Dexie upgrade-transaction table. Rows are
// untyped DB reads (the code does no shape validation), matching the pre-TS
// behaviour where these were `any`.
interface DexieLikeTable {
    get(key: unknown): Promise<any>;
    add(obj: unknown): Promise<any>;
    put(obj: unknown): Promise<any>;
}
interface DexieLikeTx {
    table(name: string): DexieLikeTable;
}
interface SeedPhaseAFeRootOptions {
    now?: () => number;
}

/**
 * Phase A migration: seed `fe_root[0]` from the legacy
 * `app_settings.syncFolderHandle` (mirror, not move). Idempotent via the
 * `fe.phase_a_seeded` sentinel. Writes an audit row at
 * `fe.audit.migration:29`.
 *
 * Crucially: the legacy `app_settings.syncFolderHandle` key is NOT deleted.
 * Sync-folder.js continues to read it as the source of truth; `fe_root[0]`
 * is a derived mirror. Rollback to v26 is data-loss-free.
 *
 * @param {object} tx Dexie transaction. Must expose `tx.table('app_settings')`
 *   and `tx.table('fe_root')` per Dexie upgrade-callback semantics.
 * @param {object} [options] For testability.
 * @param {() => number} [options.now] Override Date.now() — the audit row
 *   captures `ranAt`; tests pin a deterministic value.
 * @returns {Promise<{seeded: boolean, seededRoots: 0 | 1, alreadySeeded: boolean}>}
 *   `seeded` is true iff this call performed work. `alreadySeeded` is true iff
 *   the sentinel short-circuited (idempotency hit).
 */
export async function seedPhaseAFeRoot(tx: DexieLikeTx, options: SeedPhaseAFeRootOptions = {}) {
    const now = typeof options.now === 'function' ? options.now : Date.now;

    const settingsTable = tx.table('app_settings');

    // Idempotency: if the sentinel says we already seeded, return without
    // writing anything. Dexie may re-run the upgrade in some edge cases
    // (e.g. clearing browser data + re-importing); the sentinel makes the
    // migration safe to re-execute.
    const seededSentinel = await settingsTable.get('fe.phase_a_seeded');
    if (seededSentinel?.value === true) {
        return { seeded: false, seededRoots: 0, alreadySeeded: true };
    }

    // Read legacy source of truth. Existing writers/readers all go through
    // SyncFolderService.getHandle() at sync-folder.js:331-376, which uses
    // `db.app_settings.get('syncFolderHandle')`. We mirror — never move.
    const legacyHandleRow = await settingsTable.get('syncFolderHandle');
    const legacyHandle = legacyHandleRow?.value || null;

    let seededRoots = 0;
    if (legacyHandle) {
        const rootTable = tx.table('fe_root');
        const label = legacyHandle.name || 'Sync folder';
        await rootTable.add({
            label,
            handle: legacyHandle,
            addedAt: now(),
            enabled: true, // QM convention: true/false, not 1/0
            lastFullWalkAt: null,
        });
        seededRoots = 1;
    }

    await settingsTable.put({ key: 'fe.phase_a_seeded', value: true });
    await settingsTable.put({
        key: 'fe.audit.migration:29',
        value: {
            ranAt: now(),
            seededRoots,
            note: 'Phase A: file-explorer index stores + fe_root[0] seeded from app_settings.syncFolderHandle (mirror; legacy key preserved as source of truth).',
        },
    });

    return { seeded: true, seededRoots, alreadySeeded: false };
}
